import type { ReactNode } from 'react';
import { Box, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';

export interface ColumnDefinition<T> {
  header: string | ReactNode | ((data: T[]) => ReactNode);
  accessor: keyof T | ((item: T) => ReactNode);
  align?: 'left' | 'right' | 'center';
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export default function ResourceDataView<T extends { _id: string | number }>({items, columns, resourceName}: { items: T[]; columns: ColumnDefinition<T>[]; resourceName: string }) {
 const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const renderValue = (item: T, column: ColumnDefinition<T>) => {
    return typeof column.accessor === 'function'
      ? column.accessor(item)
      : (getNestedValue(item, column.accessor as string) as ReactNode);
  };

  const renderTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table stickyHeader sx={{ minWidth: 650 }} aria-label={`lista de ${resourceName.toLowerCase()}`}>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                key={typeof col.header === 'string' ? col.header : `header-${index}`}
                align={col.align}
                sx={{ fontWeight: 700, bgcolor: 'grey.100' }}
              >
                {typeof col.header === 'function' ? col.header(items) : col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item._id} hover>
              {columns.map((col, index) => (
                <TableCell key={typeof col.header === 'string' ? col.header : `cell-${index}`} align={col.align}>
                  {renderValue(item, col)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCards = () => (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} key={item._id} sx={{ minWidth: 0 }}>
          <Card variant="outlined" sx={{ transition: 'box-shadow 0.2s ease' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.4, p: 2 }}>
              {columns.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    pb: 1.2,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {renderValue(item, columns[0])}
                  </Box>
                </Box>
              )}

              {columns.slice(1).map((column, index) => (
                <Box
                  key={typeof column.header === 'string' ? column.header : `mobile-${index}`}
                  sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.5, sm: 2 }, alignItems: 'flex-start' }}
                >
                  <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0 }}>
                    {typeof column.header === 'function' ? column.header(items) : column.header}:
                  </Typography>
                  <Box sx={{ minWidth: 0, maxWidth: '100%', width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' }, overflowWrap: 'anywhere' }}>{renderValue(item, column)}</Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

 return isMobile ? renderCards() : renderTable();
}
